import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Input } from "antd";
import { Button } from "../ui/button";
import { ThemeToggle } from "../ui/theme-toggle";
import { useTheme } from "../../contexts/ThemeContext";
import MaitaiLogo from "../../assets/maitai_logo_full.svg";
import MaitaiLogoDark from "../../assets/maitai_logo_dark_transparent.svg";

const LoginChooser: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [isSignUp, setIsSignUp] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [email, setEmail] = useState("");
  const { theme } = useTheme();
  const personalDomains = useMemo(
    () => [
      "gmail.com",
      "yahoo.com",
      "hotmail.com",
      "outlook.com",
      "icloud.com",
      "me.com",
      "mac.com",
      "hotmail.co.uk",
      "hotmail.com.au",
    ],
    []
  );

  const normalizedEmail = email.trim().toLowerCase();
  const isEmailValid = Boolean(
    normalizedEmail && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail)
  );
  const isChooserStep = searchParams.get("step") === "choose";
  const shouldShowChooser = isChooserStep && isEmailValid;
  const isPersonalEmail = useMemo(() => {
    if (!isEmailValid) {
      return false;
    }
    const emailDomain = normalizedEmail.split("@")[1] || "";
    return personalDomains.includes(emailDomain);
  }, [isEmailValid, normalizedEmail, personalDomains]);

  const startLogin = (
    mode: "org" | "individual",
    action: "signin" | "signup"
  ) => {
    sessionStorage.setItem("auth_inflight_ts", Date.now().toString());
    setIsRedirecting(true);
    if (mode === "individual") {
      localStorage.removeItem("auth0_org_id");
    }
    localStorage.setItem("auth_mode", mode);
    localStorage.removeItem("switching_org");
    const params = new URLSearchParams(searchParams);
    params.set("start", "1");
    params.set("auth_action", action);
    window.location.href = `/login?${params.toString()}`;
  };

  const startOrgSignup = () => {
    sessionStorage.setItem("auth_inflight_ts", Date.now().toString());
    setIsRedirecting(true);
    localStorage.setItem("auth_mode", "org");
    localStorage.removeItem("auth0_org_id");
    localStorage.removeItem("switching_org");
    window.location.href = "/org-signup";
  };

  useEffect(() => {
    const emailParam = searchParams.get("email") || "";
    if (!email && emailParam) {
      setEmail(emailParam);
    }
  }, [email, searchParams]);

  if (isRedirecting) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="space-y-3 text-center">
          <p className="text-lg font-semibold text-foreground">Loading...</p>
          <p className="text-sm text-muted-foreground">
            One moment while we finish authentication.
          </p>
        </div>
      </div>
    );
  }

  const primaryMode = isPersonalEmail ? "individual" : "org";
  const secondaryMode = isPersonalEmail ? "org" : "individual";
  const primaryLabel = isPersonalEmail
    ? "Continue as personal"
    : "Continue as organization";
  const secondaryLabel = isPersonalEmail
    ? isSignUp
      ? "Sign up as organization"
      : "Sign in as organization"
    : isSignUp
    ? "Sign up as personal"
    : "Sign in as personal";

  const handlePrimary = () => {
    if (primaryMode === "org" && isSignUp) {
      startOrgSignup();
      return;
    }
    startLogin(primaryMode, isSignUp ? "signup" : "signin");
  };

  const handleSecondary = () => {
    if (secondaryMode === "org" && isSignUp) {
      startOrgSignup();
      return;
    }
    startLogin(secondaryMode, isSignUp ? "signup" : "signin");
  };

  const handleContinue = () => {
    const params = new URLSearchParams(searchParams);
    params.set("step", "choose");
    params.set("email", normalizedEmail);
    localStorage.setItem("auth0_login_hint", normalizedEmail);
    navigate(`/login?${params.toString()}`);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background relative">
      <div className="absolute top-4 left-4">
        <img
          src={theme === "dark" ? MaitaiLogoDark : MaitaiLogo}
          alt="Maitai"
          className="h-8 w-auto"
        />
      </div>
      <div className="absolute top-4 right-4">
        <ThemeToggle />
      </div>
      <div className="w-full max-w-xl space-y-6 p-8">
        <div
          className={`space-y-2 ${
            shouldShowChooser ? "text-left" : "text-center"
          }`}>
          <h1 className="text-3xl font-gc-demi-bold text-foreground">
            {shouldShowChooser
              ? `${
                  isSignUp ? "Signing up" : "Signing in"
                } as ${normalizedEmail}`
              : isSignUp
              ? "Sign up"
              : "Sign in"}
          </h1>
          {!shouldShowChooser ? (
            <p className="text-sm text-muted-foreground">
              Enter your email to continue.
            </p>
          ) : (
            <Button
              variant="link"
              className="text-primary text-sm px-0 h-auto"
              onClick={() => {
                const params = new URLSearchParams(searchParams);
                params.delete("step");
                localStorage.removeItem("auth0_login_hint");
                navigate(`/login?${params.toString()}`);
              }}>
              Change email
            </Button>
          )}
        </div>

        {!shouldShowChooser ? (
          <div className="space-y-4">
            <Input
              size="large"
              type="email"
              placeholder="you@company.com"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              className="border-border"
              style={{
                backgroundColor: "hsl(var(--background))",
                color: "hsl(var(--foreground))",
              }}
            />
            <Button
              className="w-full text-base"
              size="lg"
              disabled={!isEmailValid}
              onClick={handleContinue}>
              Continue
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <button
              type="button"
              className="w-full border rounded-xl p-6 text-left bg-card text-base font-semibold text-foreground"
              onClick={handlePrimary}>
              {primaryLabel}
            </button>
            <div className="text-left">
              <Button
                variant="link"
                className="text-primary text-sm px-0 h-auto"
                onClick={handleSecondary}>
                {secondaryLabel}
              </Button>
            </div>
          </div>
        )}
        <div className="text-center text-sm text-muted-foreground">
          {isSignUp ? "Already using Maitai?" : "New to Maitai?"}
          <Button
            variant="link"
            className="text-primary text-sm px-0 ml-1 h-auto"
            onClick={() => setIsSignUp(!isSignUp)}>
            {isSignUp ? "Sign in" : "Sign up instead"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default LoginChooser;
